import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check } from 'lucide-react'
import ErrorNote from './ErrorNote'
import {
  CARRYOVER_KEY,
  alreadyMatches,
  fetchCarryover,
  fieldName,
  inWorkingOrder,
  markCarriedOver,
  takeTheirs,
  type Carryover,
} from '../api/carryover'

/**
 * What to type into Jackrabbit (D-694, pilot item 1).
 *
 * `asked`: *"a report of changes we have made to classes or families etc that
 * she can run, so she can go back to Jackrabbit and make the updates there."*
 *
 * **A to-do list, and the tick is the only state it has.** Everything else is
 * derived from the ownership flags the schema already carries, so it is right
 * the first time it is run and there is nothing to reset.
 *
 * **It reads one direction, and says so on the screen.** An absence a parent
 * reports in Jackrabbit's own portal is invisible here — nothing imports
 * absences — so this answers *what must I carry across* and never *are the two
 * systems the same*. Left unsaid, the second reading is the natural one, and it
 * is wrong in the direction that costs money (D-209).
 */
export default function CarryoverView() {
  const queryClient = useQueryClient()

  const list = useQuery({ queryKey: CARRYOVER_KEY, queryFn: fetchCarryover })

  const done = useMutation({
    mutationFn: markCarriedOver,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CARRYOVER_KEY }),
  })

  /*
   * Its own mutation rather than a flag on the one above, so a refusal carries
   * the server's sentence — every one of them is something she can act on, and
   * folding both into one error would lose which action failed.
   */
  const taking = useMutation({
    mutationFn: takeTheirs,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CARRYOVER_KEY }),
  })

  const rows = inWorkingOrder(list.data?.rows ?? [])

  return (
    <div className="page-shell px-5 py-8">
      <h1 className="font-display text-2xl text-ink">Updates to make in Jackrabbit</h1>

      <p className="mt-2 max-w-[60ch] text-[13.5px] text-ink-2">
        Everything the studio has changed here that Jackrabbit does not know about. Tick a row once
        you have typed it in, and it goes away until it changes again.
      </p>

      {/*
        <b>The limit, said before she reads the list rather than after</b>
        (D-209). Nothing imports absences or enrolments from Jackrabbit, so a
        quiet list means "nothing to carry over", not "the two systems agree".
      */}
      <p className="mt-1 max-w-[60ch] text-[12.5px] text-ink-3">
        This only looks one way. Anything changed in Jackrabbit itself &mdash; an absence a parent
        reported there, for instance &mdash; will not appear here.
      </p>

      {list.isPending && (
        <p className="mt-6 text-[13px] text-ink-2" role="status">
          Loading&hellip;
        </p>
      )}

      {list.isError && (
        <div className="mt-6">
          <ErrorNote>This list could not be loaded. Try again in a moment.</ErrorNote>
        </div>
      )}

      {done.isError && (
        <div className="mt-6">
          <ErrorNote>{done.error instanceof Error ? done.error.message : 'That could not be saved.'}</ErrorNote>
        </div>
      )}

      {/*
        <b>The server's own sentence.</b> Every refusal here names something she
        can do — run an import, or there is nothing of theirs to take — so a
        generic message would throw away the only useful part (D-209).
      */}
      {taking.isError && (
        <div className="mt-6">
          <ErrorNote>
            {taking.error instanceof Error
              ? taking.error.message
              : "Jackrabbit's value could not be taken."}
          </ErrorNote>
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <p className="mt-6 text-[13.5px] text-ink-2">
          Nothing to carry over. Everything the studio has changed here is already in Jackrabbit.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="mt-6 flex flex-col gap-2">
          {rows.map((row) => (
            <Row
              key={`${row.subject}:${row.subjectId}:${row.field}`}
              row={row}
              onDone={done.mutate}
              onTakeTheirs={taking.mutate}
              busy={done.isPending || taking.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function Row({
  row,
  onDone,
  onTakeTheirs,
  busy,
}: {
  row: Carryover
  onDone: (row: Carryover) => void
  onTakeTheirs: (row: Carryover) => void
  busy: boolean
}) {
  const isNew = row.field === 'Exists'

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-line bg-surface px-4 py-3">
      {/*
        <b>The whole line on a phone, with the button beneath</b> (D-749).
        Sharing the line with *Done in Jackrabbit* left about half the card for
        the content, which put a Jackrabbit class name on four lines and
        squeezed both comparison boxes into a column beside white space. The
        distinguishing part of one of these names is at its end (D-225's
        wrapping note), so the narrow column is the worst place for it. Found
        by rendering at 390px, not by reading.
      */}
      <div className="min-w-0 w-full sm:w-auto sm:flex-1">
        {/* Machine-shaped and unbreakable to CSS, so it wraps anywhere. */}
        <p className="text-[14px] font-semibold wrap-anywhere text-ink">{row.name}</p>

        <p className="mt-0.5 text-[12.5px] text-ink-2">{fieldName(row.field)}</p>

        {/*
          <b>Both sides, side by side</b> (D-749). `asked`: *"for this item the
          value imported from jack rabbit was x on the left, and then on the
          right it shows the value in our system now is y."* A single value was
          the whole row before, which left her comparing it against a screen in
          another tab from memory.

          <b>It stacks on a phone rather than squeezing two columns into
          390px</b>, and the labels are what carry the meaning either way — the
          arrow between them is decoration and is hidden from a screen reader.
        */}
        {!isNew && (
          <div className="mt-1.5 grid grid-cols-1 items-stretch gap-2 sm:grid-cols-[1fr_auto_1fr]">
            <Side label="Jackrabbit has" value={row.theirs} seenAt={row.theirsSeenAt} />

            {/*
              <b>Gone once they stack, rather than turned downwards.</b> Found
              by rendering at 390px: wrapped, the arrow took a line of its own
              between two half-width boxes and pointed at the white space
              beside them. The labels are what carry the comparison at any
              width - the arrow only helps when the two are genuinely
              left-and-right, so below `sm` it is `display: none` and leaves
              the grid entirely rather than holding an empty row open.
            */}
            <span aria-hidden="true" className="hidden self-center text-[13px] text-ink-3 sm:block">
              &rarr;
            </span>

            <Side label="We have" value={row.value} seenAt={null} />
          </div>
        )}

        <p className="mt-1.5 text-[11.5px] text-ink-3">
          {isNew
            ? row.where
            : alreadyMatches(row)
              ? 'Jackrabbit already matches this — nothing to type'
              : `Type it into ${row.where}`}
        </p>
      </div>

      {/*
        <b>Its own words, not a bare tick.</b> The row is a job she has just
        done somewhere else, so the button says what pressing it claims — and
        it claims something about Jackrabbit rather than about this screen.
      */}
      {/*
        <b>Two answers, because the row has two honest ones</b> (D-811). Which
        side is newer is hers to know: she may have corrected it here, or
        somebody may have updated it there after a parent rang.

        <b>Taking theirs is only offered where there is something to take</b> —
        an import has seen the field and the two disagree. Offering it on a row
        whose sides already match would be a button that does nothing, and
        offering it where we have never seen theirs would write a blank.
      */}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {row.canTakeTheirs && (
          <button
            type="button"
            onClick={() => onTakeTheirs(row)}
            disabled={busy}
            className="flex min-h-[32px] items-center gap-1 rounded-full border border-line px-3 text-[12px] font-semibold text-ink-2"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Use Jackrabbit&rsquo;s
          </button>
        )}

        <button
          type="button"
          onClick={() => onDone(row)}
          disabled={busy}
          className="flex min-h-[32px] items-center gap-1 rounded-full border border-line px-3 text-[12px] font-semibold text-accent-2"
        >
          <Check size={14} aria-hidden="true" />
          Done in Jackrabbit
        </button>
      </div>
    </li>
  )
}

/**
 * One side of the comparison (D-749).
 *
 * **A value nobody has seen is said, not left blank.** Jackrabbit's side is
 * recorded by the import, and nothing was recorded before this shipped — so a
 * field fills in the first time an import matches its class again. An empty
 * box would read as *Jackrabbit holds nothing here*, which is a different
 * statement and the one that would send her to overwrite a correct value
 * (D-209).
 */
function Side({
  label,
  value,
  seenAt,
}: {
  label: string
  value: string | null
  seenAt: string | null
}) {
  return (
    <div className="min-w-0 rounded-lg border border-line bg-surface-2 px-3 py-2">
      <p className="text-[11px] font-bold uppercase tracking-wider text-ink-3">{label}</p>

      {value === null ? (
        <p className="mt-0.5 text-[12.5px] italic text-ink-3">Not seen in an import yet</p>
      ) : (
        <p className="mt-0.5 text-[13px] font-semibold wrap-anywhere text-ink">{value}</p>
      )}

      {seenAt !== null && (
        <p className="mt-0.5 text-[11px] text-ink-3">at the import on {onlyTheDay(seenAt)}</p>
      )}
    </div>
  )
}

/**
 * The day an import ran, without its time.
 *
 * Parsed rather than sliced so an offset is honoured: the server sends an
 * instant, and taking the first ten characters of it is the UTC day, which
 * after about 7pm Central is tomorrow (D-68, D-485).
 */
function onlyTheDay(at: string): string {
  return new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}
